import {
    CheckCircleOutlined,
    CloudSyncOutlined,
    CloudUploadOutlined
} from "@ant-design/icons";
import {
    Button,
    Card,
    Empty,
    Spin,
    Table,
    Tag,
    Tooltip
} from "antd";
import React, { useEffect, useState } from "react";
import { getAllColegios } from "../../services/colegioService";
import { syncColegios } from "../../services/colegioSyncService";
import { getSyncConfig } from "../../services/syncService";

const ColegioList = ({ refreshTrigger, onEditColegio }) => {
    const [colegios, setColegios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncingAll, setSyncingAll] = useState(false);
    const isMobile = window.innerWidth < 768;
    const [config, setConfig] = useState({
        interval: 30,
        intervalUnit: "segundos",
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    // Cargar la configuración al inicio
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const syncConfig = await getSyncConfig();
                setConfig(syncConfig);
            } catch (error) {
                console.error("Error al cargar configuración:", error);
            }
        };

        fetchConfig();
    }, []);

    // Cargar colegios al inicio y cuando cambie el trigger
    useEffect(() => {
        loadColegios();
    }, [refreshTrigger]);

    // Actualizar la lista según el intervalo configurado
    useEffect(() => {
        const interval = setInterval(() => {
            loadColegios();
        }, config.interval * (config.intervalUnit === "segundos" ? 1000 : config.intervalUnit === "minutos" ? 60000 : 3600000)); // Convertir a milisegundos

        // Limpiar intervalo al desmontar
        return () => clearInterval(interval);
    }, [config]);

    const loadColegios = async () => {
        try {
            console.log("Cargando colegios...");
            setLoading(true);
            const colegioList = await getAllColegios();
            setColegios(colegioList);
        } catch (error) {
            window.messageApi.error("Error al cargar colegios: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    // const handleDelete = async (id) => {
    //     try {
    //         await deleteColegio(id);
    //         // Actualizar la lista de colegios
    //         setColegios(colegios.filter((colegio) => colegio.id !== id));
    //         window.messageApi.success("Colegio eliminado correctamente");
    //     } catch (error) {
    //         window.messageApi.error("Error al eliminar colegio: " + error.message);
    //     }
    // };

    // const handleEdit = (colegio) => {
    //     if (onEditColegio) {
    //         onEditColegio(colegio);
    //     }
    // };

    // Renderizar el estado de sincronización
    const renderSyncStatus = (pendingSync, lastSynced) => {
        if (pendingSync === 1) {
            return (
                <Tooltip title="Pendiente de sincronizar">
                    <Tag icon={<CloudUploadOutlined />} color="warning">
                        Pendiente
                    </Tag>
                </Tooltip>
            );
        } else {
            const syncDate = lastSynced
                ? new Date(lastSynced).toLocaleString()
                : "Nunca";

            return (
                <Tooltip title={`Última sincronización: ${syncDate}`}>
                    <Tag icon={<CheckCircleOutlined />} color="success">
                        Sincronizado
                    </Tag>
                </Tooltip>
            );
        }
    };

    // Sincronizar colegios de esta pantalla
    const handleSyncAll = async () => {
        try {
            setSyncingAll(true);
            await syncColegios();
            // Recargar la lista para reflejar los cambios
            await loadColegios();
            window.messageApi.success("Colegios sincronizados correctamente");
        } catch (error) {
            window.messageApi.error("Error sincronizando colegios: " + error.message);
        } finally {
            setSyncingAll(false);
        }
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
        },
        {
            title: "No.",
            dataIndex: "No",
            key: "No",
            render: (No) => No || "Pendiente",
        },
        {
            title: "Nombre",
            dataIndex: "nombre",
            key: "nombre",
        },
        {
            title: "Estado",
            key: "syncStatus",
            render: (_, record) =>
                renderSyncStatus(record.pendingSync, record.lastSynced),
        },
        // {
        //     title: "Acciones",
        //     key: "actions",
        //     render: (_, record) => (
        //         <Space size="middle">
        //             <Button
        //                 type="primary"
        //                 icon={<EditOutlined />}
        //                 size="small"
        //                 onClick={() => handleEdit(record)}
        //             >
        //                 Editar
        //             </Button>
        //             <Popconfirm
        //                 title="¿Está seguro de que desea eliminar este colegio?"
        //                 onConfirm={() => handleDelete(record.id)}
        //                 okText="Sí"
        //                 cancelText="No"
        //             >
        //                 <Button danger icon={<DeleteOutlined />} size="small">
        //                     Eliminar
        //                 </Button>
        //             </Popconfirm>
        //         </Space>
        //     ),
        // },
    ];

    if (loading) {
        return <Spin size="large" />;
    }

    if (colegios.length === 0) {
        return <Empty description="No hay colegios registrados aún" />;
    }

    return (
        <div className="colegio-list-container">
            <Card
                title={isMobile ? "Colegios" : "Colegios Registrados"}
                extra={
                    <Button
                        icon={<CloudSyncOutlined />}
                        onClick={handleSyncAll}
                        loading={syncingAll}
                        type="primary"
                    >
                        {isMobile ? "" : "Sincronizar colegios"}
                    </Button>
                }
            >
                <Table
                    columns={columns}
                    dataSource={colegios.map((colegio) => ({ ...colegio, key: colegio.id }))}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ["5", "10", "20", "50"],
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} de ${total} elementos`,
                    }}
                    onChange={(newPagination) => setPagination(newPagination)}
                    bordered
                    size="middle"
                    scroll={{ x: "max-content" }}
                />
            </Card>
        </div>
    );
};

export default ColegioList; 